import { receiveSegments, requestSegments } from "app/actions/conversionModuleActions";
import { withAllContextHoc } from "app/components/React/HOCs/withAllContextsHOC";
import { SecondarySideBarType3 } from "app/components/sidebar3/types";
import CategoryOverviewContainer from "app/pages/conversion/CategoryOverviewContainer";
import { ConversionHomepageWrapper } from "app/pages/conversion/ConversionHomepageWrapper";
import { ConversionQueryBar } from "app/pages/conversion/ConversionQueryBar";
import { ConversionRootContainer } from "app/pages/conversion/ConversionRootContainer";
import ConversionSegmentsQueryBar from "app/pages/conversion/ConversionSegmentsQueryBar";
import { SegmentOverviewContainer } from "app/pages/conversion/SegmentOverviewContainer";
import { ConversionWizard } from "app/pages/conversion/wizard/ConversionWizard";
import type { SecondaryBarType } from "app/reducers/_reducers/SecondaryBarReducer";
import { fallback } from "app/routes/handlers/redirectFunctions/fallback";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { ConversionSegmentsService } from "app/services/conversion/ConversionSegmentsService";
import { SwRedux } from "app/services/SwRedux";
import type { RouterStateConfig } from "single-spa/react-app/router/IRouterState";

function getSegments() {
    return async () => {
        try {
            const swRedux = SwRedux.getInstance();
            const { conversionModule } = swRedux.getState();
            if (conversionModule.segments) {
                return true;
            }
            swRedux.dispatch(requestSegments());
            await ConversionSegmentsService.getInstance()
                .getAllSegmentsData()
                .then((segments) => {
                    swRedux.dispatch(receiveSegments(segments));
                })
                .catch((_error) => {});
            return true;
        } catch (_e) {
            return true;
        }
    };
}

export const conversionConfig: RouterStateConfig = {
    conversion: {
        parent: "sw",
        abstract: true,
        url: "/conversion",
        component: () => withAllContextHoc(ConversionRootContainer)({ shouldLogErrorFallback: false }),
        configId: "Conversion",
        resolve: {
            segments: getSegments(),
        },
        secondaryBarType: "ConversionAnalysis" as SecondaryBarType,
        secondaryBarType3: SecondarySideBarType3.ConversionAnalysis,
        redirectTo: redirectPipe(
            fallback({
                digitalsuite: "digitalsuite-home",
            }),
        ),
    },
    "conversion-homepage": {
        parent: "sw",
        url: "/conversion/home",
        component: ConversionHomepageWrapper,
        configId: "Conversion",
        resolve: {
            segments: getSegments(),
        },
        pageId: {
            section: "conversion",
            subSection: "homepage",
        },
        trackingId: {
            section: "Market Research",
            subSection: "Funnel Analysis",
            subSubSection: "homepage",
        },
        pageTitle: "conversion.homepage.title",
        hideSecondaryBar: true,
    },
    "conversion-wizard": {
        parent: "sw",
        url: "/conversion/wizard/:gid/:country/:duration?",
        component: withAllContextHoc(ConversionWizard),
        secondaryBarType3: SecondarySideBarType3.None,
        configId: "ConversionOverview",
        configIdDeps: ["Conversion"],
        resolve: {
            segments: getSegments(),
        },
        pageId: {
            section: "conversion",
            subSection: "wizard",
        },
        trackingId: {
            section: "Market Research",
            subSection: "Funnel Analysis",
            subSubSection: "wizard",
        },
        pageTitle: "conversion.wizard.title",
        params: {
            gid: "",
            country: "",
            duration: "",
        },
    },
    "conversion-categoryoverview": {
        parent: "conversion",
        url: "/categoryoverview/:industry/:category/:country/:duration",
        component: CategoryOverviewContainer,
        configId: "ConversionOverview",
        configIdDeps: ["Conversion"],
        pageId: {
            section: "conversion",
            subSection: "categoryoverview",
        },
        pageTitle: "conversion.categoryoverview.Title",
        defaultQueryParams: {},
        trackingId: {
            section: "Funnel Analysis",
            subSection: "Category Analysis",
            subSubSection: "",
        },
        params: {
            category: { type: "string", raw: true },
        },
        leftSubNav: ConversionQueryBar,
        isSecondaryBarOpen: true,
    },
    "conversion-customgroup": {
        parent: "conversion",
        url: "/:gid/:country/:duration",
        component: CategoryOverviewContainer,
        configId: "ConversionOverview",
        configIdDeps: ["Conversion"],
        pageId: {
            section: "conversion",
            subSection: "groupOverview",
        },
        pageTitle: "conversion.categoryoverview.Title",
        defaultQueryParams: {},
        trackingId: {
            section: "Market Research",
            subSection: "Funnel Analysis",
            subSubSection: "Group Analysis",
        },
        leftSubNav: ConversionSegmentsQueryBar,
        isSecondaryBarOpen: true,
    },
    "conversion-customsegement": {
        parent: "conversion",
        url: "/:gid/:sid/:country/:duration",
        component: SegmentOverviewContainer,
        configId: "ConversionOverview",
        configIdDeps: ["Conversion"],
        periodOverPeriodEnabled: true,
        params: {
            comparedDuration: { dynamic: false },
            tab: { dynamic: true },
        },
        pageId: {
            section: "conversion",
            subSection: "segementOverview",
        },
        trackingId: {
            section: "Market Research",
            subSection: "Funnel Analysis",
            subSubSection: "Segment Analysis",
        },
        pageTitle: "conversion.websiteoverview.Title",
        leftSubNav: ConversionSegmentsQueryBar,
    },
    "conversion-customsegement-nogroup": {
        parent: "conversion",
        url: "/nogroup/:sid/:country/:duration?comparedDuration",
        component: SegmentOverviewContainer,
        configId: "ConversionOverview",
        configIdDeps: ["Conversion"],
        periodOverPeriodEnabled: true,
        pageId: {
            section: "conversion",
            subSection: "segementOverview",
        },
        trackingId: {
            section: "Funnel Analysis",
            subSection: "conversion",
            subSubSection: "segement overview",
        },
        pageTitle: "conversion.websiteoverview.Title",
        leftSubNav: ConversionSegmentsQueryBar,
    },
};

// in order to make trailing space optional
conversionConfig["conversion-customsegement-duplicate"] = {
    ...conversionConfig["conversion-customsegement"],
    url: "/:gid/:sid/:country/:duration",
    redirectTo: "conversion-customsegement",
};
conversionConfig["conversion-customsegement-nogroup-duplicate"] = {
    ...conversionConfig["conversion-customsegement-nogroup"],
    url: "nogroup/:sid/:country/:duration",
    redirectTo: "conversion-customsegement-nogroup",
};
